import { styled } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import { Link } from 'react-router';
import {
  ComplianceCoverageCellStatus,
  ComplianceCoverageFileType,
  ComplianceCoverageMarket,
  ComplianceCoverageRow,
} from '@/lib/types';
import {
  buildCoverageCellUrl,
  ComplianceCoverageLinkState,
  getCell,
  getMarketLabel,
} from './ComplianceCoverageAdminPageHelpers';

const STATUS_SYMBOL: Record<ComplianceCoverageCellStatus, string> = {
  uploaded: '✓',
  missing: '✕',
  not_applicable: '—',
};

const STATUS_LABEL: Record<ComplianceCoverageCellStatus, string> = {
  uploaded: 'uploaded',
  missing: 'missing',
  not_applicable: 'not certified',
};

type ComplianceCoverageCellProps = {
  row: ComplianceCoverageRow;
  market: ComplianceCoverageMarket;
  fileType: ComplianceCoverageFileType;
  returnTo: string;
};

const ComplianceCoverageCell = ({
  row,
  market,
  fileType,
  returnTo,
}: ComplianceCoverageCellProps) => {
  const { status, languages } = getCell(row, market.id);
  const codes =
    status === 'uploaded' && fileType === 'certificates'
      ? languages.join(', ')
      : '';
  const ariaLabel = `${row.game_name} — ${getMarketLabel(market.name)}: ${
    STATUS_LABEL[status]
  }${codes ? `, ${codes}` : ''}`;
  const content = (
    <>
      {STATUS_SYMBOL[status]}
      {codes ? <Codes>{codes}</Codes> : null}
    </>
  );
  const isLink =
    status !== 'not_applicable' && row.studio_game_id !== null;
  const state: ComplianceCoverageLinkState = {
    complianceCoverageReturnTo: returnTo,
  };

  return (
    <Root
      data-status={status}
      aria-label={isLink ? undefined : ariaLabel}
    >
      {isLink ? (
        <CellLink
          to={buildCoverageCellUrl(
            row.studio_game_id as string,
            market.id,
            fileType,
          )}
          state={state}
          aria-label={ariaLabel}
        >
          {content}
        </CellLink>
      ) : (
        <CellContent>{content}</CellContent>
      )}
    </Root>
  );
};

const Root = styled(TableCell)(({ theme }) => ({
  minWidth: 92,
  height: 1,
  padding: 0,
  textAlign: 'center',
  fontSize: 15,
  borderBottom: `1px solid ${theme.palette.grey200}`,

  '&[data-status="uploaded"]': {
    background: theme.palette.green50,
    color: theme.palette.green700,
  },
  '&[data-status="missing"]': {
    background: theme.palette.red50,
    color: theme.palette.red500,
  },
  '&[data-status="not_applicable"]': {
    background: theme.palette.grey50,
    color: theme.palette.grey500,
  },
}));

const CellContent = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: 44,
  padding: theme.spacing(1, 1.25),
}));

const CellLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: 44,
  padding: theme.spacing(1, 1.25),
  color: 'inherit',
  textDecoration: 'none',

  '&:hover, &:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Codes = styled('span')({
  marginTop: 2,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
});

export default ComplianceCoverageCell;
